import type { CustomTemplateBoxRect } from '@lite-workspace/dto-fotoclass-templates';
import type { RectangleData } from '../../interfaces/rectangle-data.interface';
import type { NormalizerContext } from './codec-contract';

const RECT_KEYS = ['left', 'top', 'width', 'height'] as const;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Один разбор кортежа на две реакции: кодек падает на битой геометрии, мутаторы персист-формы её пропускают. */
export function tryRectFromBox(rect: CustomTemplateBoxRect | undefined): RectangleData | null {
  if (!Array.isArray(rect) || rect.length !== 4 || !rect.every(isFiniteNumber)) return null;

  const [left, top, width, height] = rect;
  return { left, top, width, height, angle: 0 };
}

/**
 * `denormalize(raw: unknown)` accepts untrusted/hand-patched JSON — a structurally malformed `rect`
 * must fail through `ctx.fail` (no safe single-value fallback exists for a missing position/size),
 * not throw a raw TypeError from inside a destructuring assignment.
 */
export function rectFromBox(rect: CustomTemplateBoxRect, ctx: NormalizerContext): RectangleData {
  return (
    tryRectFromBox(rect) ??
    ctx.fail(`box.rect must be a [left, top, width, height] tuple of finite numbers, got ${JSON.stringify(rect)}`)
  );
}

/**
 * Coerces a non-finite coordinate to 0 with a warn: `box.rect` is a tuple of numbers by schema, so a
 * legacy `null`/`NaN` geometry would otherwise be encoded into a v1 document that `rectFromBox` then
 * refuses to decode — an unreadable book, not a lossy one. A zero-sized box draws nothing, exactly as
 * the broken source did.
 */
export function finiteOrZero(value: number | undefined, ctx: NormalizerContext, field: string): number {
  if (isFiniteNumber(value)) return value;
  ctx.warn(`${field} is not a finite number (${JSON.stringify(value)}), coerced to 0`);
  return 0;
}

/**
 * Тот же закон, что `rectToBox`/`finiteOrZero` применяют на кодировании, — для мутаторов, которые
 * собирают персист-кортеж сами и кодировщика на пути не имеют. Молча: канала issue у мутатора нет,
 * а цена пропуска несимметрична — `rectFromBox` на чтении нечисло не прощает (`ctx.fail`), и книга
 * становится нечитаемой целиком вместо одного нулевого бокса.
 */
export function finiteRectTuple(rect: CustomTemplateBoxRect): CustomTemplateBoxRect {
  const [left, top, width, height] = rect;
  if (isFiniteNumber(left) && isFiniteNumber(top) && isFiniteNumber(width) && isFiniteNumber(height)) return rect;

  return [
    isFiniteNumber(left) ? left : 0,
    isFiniteNumber(top) ? top : 0,
    isFiniteNumber(width) ? width : 0,
    isFiniteNumber(height) ? height : 0,
  ];
}

export function rectToBox(rect: RectangleData, ctx: NormalizerContext): CustomTemplateBoxRect {
  if (RECT_KEYS.every(key => isFiniteNumber(rect[key]))) {
    return [rect.left, rect.top, rect.width, rect.height];
  }

  return [
    finiteOrZero(rect.left, ctx, 'rect.left'),
    finiteOrZero(rect.top, ctx, 'rect.top'),
    finiteOrZero(rect.width, ctx, 'rect.width'),
    finiteOrZero(rect.height, ctx, 'rect.height'),
  ];
}
